import { ApiError, isApiError, toApiError } from "../data/errors";
import { errorMessageKey } from "../contacts/errors";

/**
 * The refusal codes of the 2D endpoints (visit report, history, approvals, bell) that need their own sentence; each
 * has `visits.errors.<code lower-case>` (under `next.`). Codes 2B/2C already word (CONTACT_NOT_FOUND,
 * UPDATE_NOT_ALLOWED, CONTACT_INFO_NOT_ALLOWED, PARTS_DELETE_NOT_ALLOWED, DELETE_NOT_ALLOWED, OVER_CONTACT_LIMIT)
 * keep their `contacts.errors.*` sentence.
 */
export const VISIT_ERROR_CODES = [
  // Report form, filing, history
  "REPORT_NOT_ALLOWED",
  "HISTORY_NOT_ALLOWED",
  "REPORT_NOT_FOUND",
  "REPORT_NOT_EDITABLE",
  "REPORT_NOT_DELETABLE",
  "IDEMPOTENCY_KEY_REUSED",
  "IDEMPOTENCY_KEY_IN_USE",
  "UNDO_LEFT_TOWN_NOT_ALLOWED",
  "VIEW_ONLY_REPORT_ONLY",
  // Approvals
  "REQUEST_NOT_FOUND",
  "REQUEST_ALREADY_ANSWERED",
  "REQUEST_STALE",
  "LAST_PERSON",
  "REQUEST_NOT_APPLICABLE",
  "NOTHING_TO_CHANGE",
  "CONTACT_CHANGED",
  "CONTACT_LIMIT_REACHED",
  // Updates feed
  "UPDATE_NOT_FOUND",
  "CONTACT_LEFT_TOWN",
  "NO_SCHEDULE",
] as const;
export type VisitErrorCode = (typeof VISIT_ERROR_CODES)[number];

const KNOWN = new Set<string>(VISIT_ERROR_CODES);

const keyOf = (e: ApiError): string =>
  e.code && KNOWN.has(e.code) ? `visits.errors.${e.code.toLowerCase()}` : errorMessageKey(e);

/**
 * Translation key (under `next.`) for a 2D refusal: its own sentence for a 2D code, else 2B's (`contacts.errors.*`
 * for their codes and a bare 403, `errors.network` with no response, `errors.server` for 5xx, `errors.generic` for
 * anything else). Never the server's English.
 */
export const reportErrorKey = (code?: string, status?: number | null): string => {
  const s = status ?? null;
  return keyOf({
    status: s,
    kind: s === null ? "network" : s >= 500 ? "server" : s >= 400 ? "client" : "unknown",
    message: "",
    fieldErrors: {},
    code,
    raw: null,
  });
};

/** The sentence to show for any thrown value. */
export const reportErrorText = (err: unknown, t: (key: string) => string): string =>
  t(keyOf(isApiError(err) ? err : toApiError(err)));

const body = (e: ApiError): Record<string, unknown> | undefined =>
  (e.raw as { response?: { data?: Record<string, unknown> } } | null)?.response?.data;

/**
 * 409 REQUEST_ALREADY_ANSWERED: how it was answered and by whom. `answered_elsewhere` (answered in today's panel)
 * and `withdrawn` (the report was edited or deleted, or the contact deleted) come with `answeredBy: null`. `decidedAt`: when.
 */
export const alreadyAnsweredOf = (
  err: unknown,
): { status: string; answeredBy: { id: number; name: string } | null; decidedAt: string | null } | null => {
  if (!isApiError(err) || err.status !== 409 || err.code !== "REQUEST_ALREADY_ANSWERED") return null;
  const b = body(err);
  const by = b?.answered_by as { id?: unknown; name?: unknown } | null | undefined;
  return {
    status: typeof b?.status === "string" ? b.status : "",
    answeredBy: by && typeof by.id === "number" && typeof by.name === "string" ? { id: by.id, name: by.name } : null,
    // ISO date-time of the first answer (newer servers); null when not sent.
    decidedAt: typeof b?.decided_at === "string" && b.decided_at ? b.decided_at : null,
  };
};

/** 403 CONTACT_LIMIT_REACHED / OVER_CONTACT_LIMIT: the plan's numbers, else null. */
export const limitOf = (err: unknown): { count: number; limit: number } | null => {
  if (!isApiError(err) || err.status !== 403 || (err.code !== "CONTACT_LIMIT_REACHED" && err.code !== "OVER_CONTACT_LIMIT")) return null;
  const b = body(err);
  const count = Number(b?.current_count);
  const limit = Number(b?.limit);
  return b?.current_count != null && b?.limit != null && Number.isFinite(count) && Number.isFinite(limit) ? { count, limit } : null;
};
